import { useEffect } from 'react';
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, Flex } from '@chakra-ui/react';
import { Controller, Form, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';

import { AlertOutlineIcon, CloseIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { TextBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputComponent from 'UiKit/input';
import Tooltip from 'UiKit/tooltip';
import InputCurrency from 'UiKit/input-currency';
import InputPercentage from 'UiKit/input-percentage';
import InputYearMonthPicker from 'UiKit/input-year-month-picker';
import InputSelectComponent from 'UiKit/input-select';

import useGetBrandList from 'Domain/target/useGetBrandList';
import usePostTarget from 'Domain/target/usePostTarget';
import usePatchTarget from 'Domain/target/usePatchTarget';
import { useTargetState } from 'View/target/state';
import EmptyStateForm from 'View/target/component/empty-state-form';
import useCloseModalRestriction from 'View/target/hooks/useCloseModalRestriction';
import checkShowAllFields from 'View/target/helper/checkShowAllFields';
import checkFormUpsertValidation from 'View/target/helper/checkFormUpsertValidation';
import populateFormFields from 'View/target/helper/populateFormFields';
import { defaultValues } from './const';

import styles from './styles.module.css';

const FormUpsert = ({ t }) => {
  const { state, updateState } = useTargetState();
  const { watch, formState, control, reset, setValue } = useForm({ defaultValues });
  const { onCloseRestriction } = useCloseModalRestriction();

  const formField = watch();
  const { errors, isSubmitting, isDirty } = formState;

  const { data: brandOptions, isLoading: isLoadingBrand } = useGetBrandList(formField?.appliedAt);
  const { mutate: createTarget, isPending: isCreating } = usePostTarget();
  const { mutate: updateTarget, isPending: isUpdating } = usePatchTarget();

  const isEditForm = !isEmpty(state?.selectedTarget);
  const isLoading = isSubmitting || isCreating || isUpdating;

  const isShowAllFields = checkShowAllFields(formField);
  const isValidForm = checkFormUpsertValidation({ formField, isShowAllFields });

  const onClose = () => {
    if (isDirty)
      onCloseRestriction({
        submit: () => {
          updateState({ isOpenFormUpsert: false, selectedTarget: null });
          reset();
        },
      });
    else {
      updateState({ isOpenFormUpsert: false, selectedTarget: null });
      reset();
    }
  };

  const resetForm = () => {
    updateState({ isOpenFormUpsert: false, selectedTarget: {} });
    reset();
  };

  const handleSubmit = (form) => {
    const { data, event } = form;
    event.preventDefault();

    const onSuccess = () => resetForm();

    if (isEditForm) updateTarget({ id: state?.selectedTarget.id, formData: data }, { onSuccess });
    else createTarget(data, { onSuccess });
  };

  useEffect(() => {
    if (isEditForm) populateFormFields({ setValue, selectedTarget: state?.selectedTarget });
  }, [isEditForm, state?.selectedTarget, setValue]);

  return (
    <Modal isOpen={state?.isOpenFormUpsert} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth="1000px">
        <ModalHeader className={styles['form__header']}>
          <TextBold className={styles['form__title-text']}>
            <Condition statement={state?.isEditFormUpsert} fallback={t('createButton')}>
              {`Edit ${t('form.titleTarget')}`}
            </Condition>
          </TextBold>

          <CloseIcon color="var(--color-semantic-text)" size="22px" onClick={onClose} />
        </ModalHeader>
        <ModalBody className={styles['form-body__container']}>
          <Form control={control} className={styles['form__container']} onSubmit={handleSubmit}>
            <Box className={styles['form-column__container']}>
              <Flex gap="20px" width="50%">
                <Box width="50%">
                  <InputYearMonthPicker
                    label={t('form.labelMonthYear')}
                    placeholder={t('form.placeholderMonthYear')}
                    isDisabled={isEditForm}
                    error={errors?.appliedAt}
                    value={formField.appliedAt}
                    onChange={(value) => setValue('appliedAt', value, { shouldDirty: true })}
                    icon="calendar"
                    containerClassname={styles['form__container--yearmonthpicker']}
                  />
                </Box>
                <Condition statement={isShowAllFields}>
                  <Box width="50%">
                    <Controller
                      control={control}
                      name="brandId"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <InputSelectComponent
                          className={styles['form-body__input']}
                          error={errors?.brandId}
                          label={t('form.labelBrand')}
                          value={formField?.brandId}
                          placeholder={t('form.placeholderBrand')}
                          options={brandOptions}
                          isMulti={false}
                          field={{
                            ...field,
                            onChange: (value) => {
                              field.onChange(value);
                            },
                          }}
                          isLoading={isLoadingBrand}
                          minHeight="34px"
                          isDisabled={isEditForm}
                        />
                      )}
                    />
                  </Box>
                </Condition>
              </Flex>
              <Box width="50%">
                <Condition statement={isShowAllFields}>
                  <Controller
                    control={control}
                    name="targetGmv"
                    render={() => (
                      <InputCurrency
                        className={styles['form-body__input']}
                        label={t('form.labelTargetIncome')}
                        placeholder={0}
                        value={formField?.targetGmv}
                        error={errors?.targetGmv}
                        onChange={(value) => setValue('targetGmv', value, { shouldDirty: true })}
                        rightLabel={
                          <Tooltip
                            align="left"
                            label={t('form.targetIncomeTooltip')}
                            children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                          />
                        }
                        rightLabelPosition="normal"
                        height="34px"
                      />
                    )}
                  />
                </Condition>
              </Box>
            </Box>
            <Condition statement={isShowAllFields} fallback={<EmptyStateForm t={t} />}>
              <Flex gap="48px" marginTop="32px">
                <Box className={styles['form-inner-col__container']}>
                  <TextBold fontSize="var(--font-heading8)">{`${t('form.titleTarget')} Shopee`}</TextBold>
                  <Box className={styles['form-row__container']}>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetViewsShopee"
                        render={() => (
                          <InputComponent
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetViews')} Shopee`}
                            placeholder={t('form.placeholderTargetViews')}
                            value={formField?.targetViewsShopee}
                            error={errors?.targetViewsShopee}
                            onChange={(value) => setValue('targetViewsShopee', value, { shouldDirty: true })}
                            isNumber={true}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipViews')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetCtrShopee"
                        render={() => (
                          <InputPercentage
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetCtr')} Shopee`}
                            placeholder={t('form.placeholderTargetViews')}
                            value={formField?.targetCtrShopee}
                            error={errors?.targetCtrShopee}
                            onChange={(value) => setValue('targetCtrShopee', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipCtr')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box className={styles['form-row__container']}>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetAovShopee"
                        render={() => (
                          <InputCurrency
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetAov')} Shopee`}
                            placeholder={0}
                            value={formField?.targetAovShopee}
                            error={errors?.targetAovShopee}
                            onChange={(value) => setValue('targetAovShopee', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipAov')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetCtorShopee"
                        render={() => (
                          <InputPercentage
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetCtor')} Shopee`}
                            placeholder={0}
                            value={formField?.targetCtorShopee}
                            error={errors?.targetCtorShopee}
                            onChange={(value) => setValue('targetCtorShopee', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipCtor')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className={styles['form-inner-col__container']}>
                  <TextBold fontSize="var(--font-heading8)">{`${t('form.titleTarget')} Tiktok`}</TextBold>
                  <Box className={styles['form-row__container']}>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetViewsTiktok"
                        render={() => (
                          <InputComponent
                            value={formField?.targetViewsTiktok}
                            onChange={(value) => setValue('targetViewsTiktok', value, { shouldDirty: true })}
                            label={`${t('form.labelTargetViews')} Tiktok`}
                            placeholder={t('form.placeholderTargetViews')}
                            isNumber={true}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipViews')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetCtrTiktok"
                        render={() => (
                          <InputPercentage
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetCtr')} Tiktok`}
                            placeholder={0}
                            value={formField?.targetCtrTiktok}
                            error={errors?.targetCtrTiktok}
                            onChange={(value) => setValue('targetCtrTiktok', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipCtr')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box className={styles['form-row__container']}>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetAovTiktok"
                        render={() => (
                          <InputCurrency
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetAov')} Tiktok`}
                            placeholder={0}
                            value={formField?.targetAovTiktok}
                            error={errors?.targetAovTiktok}
                            onChange={(value) => setValue('targetAovTiktok', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipAov')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                    <Box width="50%">
                      <Controller
                        control={control}
                        name="targetCtorTiktok"
                        render={() => (
                          <InputPercentage
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetCtor')} Tiktok`}
                            placeholder={0}
                            value={formField?.targetCtorTiktok}
                            error={errors?.targetCtorTiktok}
                            onChange={(value) => setValue('targetCtorTiktok', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipCtor')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box className={styles['form-row__container']}>
                    <Box width="calc(50% - 10px)">
                      <Controller
                        control={control}
                        name="targetErrTiktok"
                        render={() => (
                          <InputPercentage
                            className={styles['form-body__input']}
                            label={`${t('form.labelTargetErr')} Tiktok`}
                            placeholder={0}
                            value={formField?.targetErrTiktok}
                            error={errors?.targetErrTiktok}
                            onChange={(value) => setValue('targetErrTiktok', value, { shouldDirty: true })}
                            rightLabel={
                              <Tooltip
                                align="left"
                                label={t('form.tooltipErr')}
                                children={<AlertOutlineIcon style={{ marginTop: '2px' }} size="14px" color="#215AA8" />}
                              />
                            }
                            rightLabelPosition="normal"
                            height="34px"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </Flex>
            </Condition>
            <Box marginTop="63px">
              <Flex gap={'21px'} justifyContent="end">
                <ButtonOutline type="button" className={styles['form__button--cancel']} color="#000" onClick={onClose}>
                  {t('form.buttonCancel')}
                </ButtonOutline>
                <ButtonSolid
                  type="submit"
                  className={styles['form__button--submit']}
                  disabled={isEditForm ? isLoading || !isValidForm || !isDirty : isLoading || !isValidForm}
                >
                  {t('form.buttonSave')}
                </ButtonSolid>
              </Flex>
            </Box>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FormUpsert;
